import { Component, ChangeDetectionStrategy, computed, inject, signal } from '@angular/core';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { DatePipe } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { WidgetsService } from '@components/widgets/widgets.service';
import { marker as _translate } from '@biesbjerg/ngx-translate-extract-marker';
import { VehicleStore } from '@modules/core/store/vehicle.store';
import { EvSocPrognosisStore } from '@modules/core/store/ev-soc-prognosis.store';
import { TranslateService } from '@modules/core/services/translate.service';
import { TranslateModule } from '@ngx-translate/core';
import { Vehicle } from '@modules/shared/models/vehicle';
import { SpinnerComponent } from '@modules/shared/components/spinner/spinner.component';
import { BaseWidgetDirective } from '@components/widgets/widgetsbase.directive';
export interface EvSocItem {
    rowId: string;
    inventoryNumber: string;
    vehicle: Vehicle;
    prognosisSoc?: number;
    plannedArrival?: Date;
    warningTime?: Date;
}

@Component({
    selector: 'app-ev-soc-widget',    
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpinnerComponent, TranslateModule, DatePipe, BaseWidgetDirective, DragDropModule],
    templateUrl: './ev-soc-widget.component.html',
    styleUrls: ['./ev-soc-widget.component.scss'],
})
export class EvSocWidgetComponent {
    public readonly widgetState = inject(WidgetsService);
    private readonly _ts = inject(TranslateService);
    private readonly _vehicleStore = inject(VehicleStore);
    private readonly _prognosisStore = inject(EvSocPrognosisStore);

    public readonly widgetTitle = signal<string>('');
    public readonly widgetId = signal<string>('widget-ev-soc');
    public readonly fillRemaining = signal<boolean>(true);
    public readonly fixedSize = signal<boolean>(false);
    public readonly defaultFraction = signal<number>(0.4);
    public readonly sortCol = signal<'inv' | 'route' | 'warn' | 'depot' | 'soc'>('warn');
    public readonly sortDir = signal<'asc' | 'desc'>('asc');

    public readonly evItems = computed<EvSocItem[] | undefined>(() => {
        const vehicles = this._vehicles();
        if (vehicles == undefined) {
            return undefined;
        }

        const prognosisRows = this._prognosisStore.rows();

        const col = this.sortCol();
        const dir = this.sortDir();
        const vehicleLookup = new Map(vehicles.map(vehicle => [vehicle.inventoryNumber, vehicle] as const));

        const items = prognosisRows.map((row) => {
            const vehicle = vehicleLookup.get(row.inventoryNumber) ?? new Vehicle({ inventoryNumber: row.inventoryNumber });

            return {
                rowId: row.rowId,
                inventoryNumber: row.inventoryNumber,
                vehicle,
                prognosisSoc: row.warning.stateOfChargeAtDepot,
                plannedArrival: row.warning.depotTime,
                warningTime: row.warning.startTime,
            };
        });

        items.sort((a, b) => {
            let cmp = 0;
            switch (col) {
                case 'inv':   cmp = a.inventoryNumber.localeCompare(b.inventoryNumber); break;
                case 'route': cmp = (a.vehicle?.routeNumber ?? '').localeCompare(b.vehicle?.routeNumber ?? ''); break;
                case 'warn': {
                    const ta = a.warningTime ? a.warningTime.getTime() : Infinity;
                    const tb = b.warningTime ? b.warningTime.getTime() : Infinity;
                    cmp = ta - tb;
                    break;
                }
                case 'depot': {
                    const ta = a.plannedArrival ? a.plannedArrival.getTime() : Infinity;
                    const tb = b.plannedArrival ? b.plannedArrival.getTime() : Infinity;
                    cmp = ta - tb;
                    break;
                }
                case 'soc':   cmp = (a.vehicle?.stateOfCharge?.sensorValue ?? Infinity) - (b.vehicle?.stateOfCharge?.sensorValue ?? Infinity); break;
            }
            if (cmp === 0) {
                cmp = a.rowId.localeCompare(b.rowId);
            }

            return dir === 'asc' ? cmp : -cmp;
        });

        return items;
    });

    private readonly _vehicles = toSignal(
        this._vehicleStore.getVehiclesState(),
        { initialValue: undefined }
    );

    constructor() {
        this.widgetTitle.set(this._ts.instant(_translate('Low soc prognosis warning')));
    }

    public setSort(col: 'inv' | 'route' | 'warn' | 'depot' | 'soc'): void {
        if (this.sortCol() === col) {
            this.sortDir.update(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            this.sortCol.set(col);
            this.sortDir.set('asc');
        }
    }

}
